 
import React from "react";
import { Box, Button, Link } from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { LoadState } from "../../../../../../../constants/enums";
import { StandardTableActions } from "../../../../../../../components/StandardTableActions/StandardTableActions";
import { fetchCommAutomobileScheduleAListAsync } from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoScheduleActions";
import { ICommAutomobileScheduleA } from "../../../../../../../redux/commAutoPolicy/commAutoSchedule/commAutoSchedule.types";
import { StatusRenderer } from "../../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { useTableV2State } from "../../../../../../../components/TableV2/hooks/useTableV2State";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { ControlledCustomSelect } from "../../../../../../../components/formsComponents";
import { COMMON_STATUS } from "../../../../../../../constants/constants";
import { PageTitleBar } from "../../../../../../../components/PageTitleBar/PageTitleBar";
import { DataTableV2 } from "../../../../../../../components/TableV2/DataTableV2";

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Safe wrapper around `useOutletContext` that returns `undefined` instead of
 * throwing when the list is not rendered under a React Router `<Outlet>` (the
 * case inside the Lead Comm Auto panel). In the customer-policy context the
 * outlet tuple is `[customerId, customerPolicyId]`.
 */
const useOutletContextSafe = (): unknown[] | undefined => {
  try {
    return useOutletContext<unknown[]>();
  } catch {
    return undefined;
  }
};

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Optional lead-scope props. All are additive and optional so the
 * customer-policy route rendering (`useOutletContext` + `navigate`) is
 * unchanged when none are supplied (Requirements 16.1, 16.2, 16.3).
 *
 * - `leadId`: when supplied the list is fetched keyed by `lead_id`.
 * - `onCreate` / `onEdit`: optional in-panel create/edit callbacks that
 *   replace route navigation in lead scope (Requirements 4.1, 9.2).
 */
interface ICommAutomobileScheduleAListProps {
  customerId?: number;
  customerPolicyId?: number;
  leadId?: string;
  onCreate?: () => void;
  onEdit?: (incrementalNumber: number | null, vehicleNumber?: number) => void;
}

export const CommAutomobileScheduleAList: React.FC<
  ICommAutomobileScheduleAListProps
> = (props) => {
  const {
    customerId: propCustomerId,
    customerPolicyId: propCustomerPolicyId,
    leadId,
    onCreate,
    onEdit,
  } = props;

  const outlet = useOutletContextSafe();
  const customerId = (propCustomerId ?? outlet?.[0]) as any;
  const customerpolicyId = (propCustomerPolicyId ?? outlet?.[1]) as any;

  const navigate = useNavigate();
  const { listData: data, listLoading: loading } = useSelector(
    (storeState: IStoreState) => storeState.commAuto.schedule.scheduleA
  );

  const dispatch = useDispatchWrapper();

  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "ACTIVE"
  );

  const {
    state: { dateState, searchState, tabs, columnsConfig, pagination,
      dense },
    setDateState,
    setSelectedTab,
    setTableTabs,
    setSearchState,
    setColumnVisibility,
 
    onDenseToggle, } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "allTimes",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1
    },
  });

  const handleCreate = () => {
    if (onCreate) {
      onCreate();
      return;
    }
    navigate(
      `/comm-automobile-policy/${customerId}/vehicle-coverage-schedule-tab/${customerpolicyId}/scheduleA`
    );
  };

  const handleEdit = (
    incrementalNumber: number | null,
    vehicleNumber?: number
  ) => {
    if (onEdit) {
      onEdit(incrementalNumber, vehicleNumber);
      return;
    }
    navigate(
      `/comm-automobile-policy/${customerId}/vehicle-coverage-schedule-tab/${customerpolicyId}/scheduleA/${incrementalNumber}/${vehicleNumber}`
    );
  };

  const fetchList = () => {
    // Lead scope wins: fetch keyed by `lead_id`, never `customer_policy_id`.
    if (leadId) {
      dispatch(fetchCommAutomobileScheduleAListAsync(0, status, leadId));
    } else {
      dispatch(fetchCommAutomobileScheduleAListAsync(customerpolicyId, status));
    }
  };

  const commautomobileApplicationTableProps: IDataTableV2Props = {
    isPagination: true,
    isDataLoading: loading === LoadState.InProgress,
    rowsPerPageOptions: pagination.rowsInPerPage,
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {},
      rightItems: {
        customPlugins: [
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS}
              />
            ),
          },
        ],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        headingAlign: "center",
        fieldName: "",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutomobileScheduleA) => {
          return (
            <StandardTableActions
              onEditClick={() =>
                handleEdit(
                  row.incremental_number,
                  row.vehicle_number ?? undefined
                )
              }
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutomobileScheduleA) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "customer_policy_id",
        headerName: "Customer Policy Id",
        fieldName: "customer_policy_id",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: ICommAutomobileScheduleA) => {
          return (
            <Link
              sx={{ cursor: "pointer" }}
              onClick={() =>
                handleEdit(
                  row.incremental_number,
                  row.vehicle_number ?? undefined
                )
              }
            >
              {row.customer_policy_id}
            </Link>
          );
        },
      },
      {
        key: "incremental_number",
        headerName: "#",
        fieldName: "incremental_number",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      {
        key: "vehicle_number",
        headerName: "Vehicle Number",
        fieldName: "vehicle_number",
        renderType: DataTableV2RowRenderType.TEXT,
      },
      
      {
        key: "coverage_number",
        headerName: "Coverage Number",
        fieldName: "coverage_number",
        renderType: DataTableV2RowRenderType.TEXT,
      },
    ],
    rows: data || [],
      dense: dense,
    onDenseToggle: onDenseToggle,};

  React.useEffect(() => {
    fetchList();
  }, [customerpolicyId, leadId, status]);

  return (
    <Box sx={{ mt: 2 }}>
      <PageTitleBar
        heading="Coverage Summary"
        rightHeading={
          <Button variant="contained" onClick={handleCreate}>
            Create
          </Button>
        }
      />
      <DataTableV2 {...commautomobileApplicationTableProps} />
    </Box>
  );
};
